import { NgModule } from '@angular/core';
import { Router, RouterModule, Routes, UrlSegment } from '@angular/router';
import { AjsNgxGuard, AuthGuard, AuthLinkGuard, HomeNavigationResolver, UnsavedChangesGuard } from '@app/core/guards';
import { AJSService } from '@app/core/services';
import { PageNotFoundComponent } from '@app/shared/components';
import { AngularJSComponent } from '@app/shared/layout/app/ajs.component';
import { HomeComponent } from '.';
import { LoginComponent } from './auth/login/login.component';
import { SwitchUnitLoginComponent } from './auth/switch-unit-login/switch-unit-login.component';
import { SchoolBookkeepingHomeComponent } from './school-bookkeeping/home/home.component';
import { AlternateHostServerSetupComponent } from './system-settings/global-setup/alternate-host-server-setup/alternate-host-server-setup.component';
import { AccountSetupComponent } from './system-settings/system-setup/account-setup/account-setup.component';
import { UserDetailsComponent } from './system-settings/user-management/users/details/details.component';
import { UserListComponent } from './system-settings/user-management/users/list/list.component';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';

const routes: Routes = [
	{ path: '', redirectTo: 'home', pathMatch: 'full' },
	{ path: '/', redirectTo: 'home', pathMatch: 'full' },
	{ path: 'dashboard', redirectTo: 'home', pathMatch: 'full' },
	{
		path: 'home',
		component: HomeComponent,
		resolve: { navigated: HomeNavigationResolver },
		canActivate: [AjsNgxGuard],
	},
	{
		path: 'login',
		component: LoginComponent,
	},
	{
		path: 'health/live',
		loadComponent: () => import('./health-check/health-check.component').then((m) => m.HealthCheckComponent),
	},
	// The routes for unauthorized and forbidden are only expected to be called for OIDC failures
	{
		path: 'unauthorized',
		component: UnauthorizedComponent,
	},
	{
		path: 'forbidden',
		component: UnauthorizedComponent,
	},
	{
		path: 'switch-unit',
		component: SwitchUnitLoginComponent,
		canActivate: [AuthGuard, AjsNgxGuard],
	},
	{
		path: 'document',
		loadChildren: () => import('@app/modules/doc-management/doc-management.module').then((m) => m.DocManagementModule),
		canActivate: [AuthGuard, AjsNgxGuard],
	},
	{
		path: 'lsb',
		children: [
			{
				path: '',
				pathMatch: 'full',
				component: SchoolBookkeepingHomeComponent,
			},
			{
				path: 'manage-accounts',
				loadChildren: () =>
					import('app/modules/school-bookkeeping/manage-accounts/manage-accounts.routes').then((m) => m.MANAGE_ACCOUNTS_ROUTES),
			},
			{
				path: 'accounts-payable',
				loadChildren: () =>
					import('app/modules/school-bookkeeping/accounts-payable/accounts-payable.routes').then((m) => m.ACCOUNTS_PAYABLE_ROUTES),
			},
			{
				path: 'cash',
				loadChildren: () =>
					import('app/modules/school-bookkeeping/cash-management/sa-cash-management.module').then((m) => m.SACashManagementModule),
			},
			{
				path: 'reports',
				loadChildren: () => import('app/modules/school-bookkeeping/reports/reports.routes').then((m) => m.LSB_REPORTS_ROUTES),
			},
		],
		canActivate: [AuthGuard, AjsNgxGuard],
	},
	{
		path: 'lsb/system-settings',
		loadChildren: () => import('@app/modules/system-settings/system-settings.module').then((m) => m.SystemSettingsModule),
		canActivate: [AuthGuard, AjsNgxGuard],
		canActivateChild: [AuthLinkGuard],
	},
	{
		path: 'lsb/systemSettings/users',
		component: UserListComponent,
		canActivate: [AuthGuard, AjsNgxGuard],
	},
	{
		path: 'lsb/systemSettings/users/:id',
		component: UserDetailsComponent,
		canActivate: [AuthGuard, AjsNgxGuard],
		canDeactivate: [UnsavedChangesGuard],
	},
	{
		path: 'lsb/globalSettings/server-ngx',
		component: AlternateHostServerSetupComponent,
		canActivate: [AuthGuard, AjsNgxGuard],
		canDeactivate: [UnsavedChangesGuard],
	},
	{
		path: 'lsb/systemSettings/account-ngx',
		component: AccountSetupComponent,
		canActivate: [AuthGuard, AjsNgxGuard],
		canDeactivate: [UnsavedChangesGuard],
	},
	{ path: '**', component: PageNotFoundComponent },
];

@NgModule({
	imports: [RouterModule.forRoot(routes, { enableTracing: false })],
	exports: [RouterModule],
})
export class AppRoutingModule {
	constructor(
		private readonly router: Router,
		private readonly ajsService: AJSService,
	) {
		this.router.config.unshift({
			matcher: (url: UrlSegment[]) => {
				const route = url.map((x) => x.path).join('/');
				const matches = url.length > 0 && this.ajsService.isAuthenticatedAJSRoute(route) ? { consumed: url } : null;
				return matches;
			},
			component: AngularJSComponent, // uses an empty component with a matcher to emulate AngularJS running within Angular routing
			canActivate: [AuthGuard, AjsNgxGuard],
		});
	}
}
